import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotificationContext } from "@/features/notifications/context/NotificationContext";
import { useStreamChat } from "@/features/chat/context/StreamChatContext";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import type { Module } from "@/config/roles";

interface MobileNavItem {
  to: string;
  labelKey: string;
  icon: string;
  /** If set, canRead(requireModule) must return true for the item to appear */
  requireModule?: Module;
  /**
   * Company feature flag. For full-access users this check is skipped.
   * For restricted officers and employees the flag must be truthy.
   */
  companyFlag?: "company_ticketing" | "company_flight_tracker" | "company_form";
  /** If true, the item is hidden from non-officers (employees / USER type) */
  officerOnly?: boolean;
}

const mainNavItems: MobileNavItem[] = [
  { to: "/",          labelKey: "home.title",       icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" },
  { to: "/chat",      labelKey: "chat.add_message", icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" },
  { to: "/leave",     labelKey: "leave.title",      icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", requireModule: "leave",   companyFlag: "company_form" },
  { to: "/inquiries", labelKey: "inquiry.title",    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", requireModule: "inquiries", companyFlag: "company_ticketing" },
];

const otherNavItems: MobileNavItem[] = [
  { to: "/return-home",   labelKey: "return_home.title",  icon: "M17.8 19.2L16 11l3.5-3.5C20.3 6.7 20.3 5.3 19.5 4.5 18.7 3.7 17.3 3.7 16.5 4.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5L2.8 8.3c-.3.5-.1 1.1.4 1.3L9 12l-2 2H4l-1 1 3 2 2 3 1-1v-3l2-2 2.4 5.8c.2.5.8.7 1.3.4l1.6-.9c.4-.2.6-.6.5-1.1z", requireModule: "flight",    companyFlag: "company_flight_tracker" },
  { to: "/files",         labelKey: "sharepoint.title",   icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z", requireModule: "sharepoint" },
  { to: "/notifications", labelKey: "notifications.title",icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" },
  { to: "/settings",      labelKey: "settings.title",     icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" },
  { to: "/users",         labelKey: "user_management.nav_title",  icon: "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75", requireModule: "profile",         officerOnly: true },
  { to: "/access/roles",  labelKey: "access.roles.nav_title",     icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z", requireModule: "role_management", officerOnly: true },
  { to: "/other-setup",   labelKey: "other_setup.title",          icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4", requireModule: "company",         officerOnly: true },
];

export const MobileNav = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { canRead, isOfficer, isFullAccess } = usePermissions();
  const { unreadCount } = useNotificationContext();
  const { unreadChatCount } = useStreamChat();
  const [othersOpen, setOthersOpen] = useState(false);
  const location = useLocation();

  const filterItem = (item: MobileNavItem): boolean => {
    // Officer-only items are hidden from employees regardless of other checks
    if (item.officerOnly && !isOfficer) return false;
    // Module-gated items: employees always pass (canRead returns true for them)
            if (item.to === "/" && userData?.userType?.toLowerCase() === "souser" && !canRead("announcements")) return false;
    if (item.requireModule && !canRead(item.requireModule)) return false;
    // Company feature flag: full-access users always see; everyone else needs the flag
    if (item.companyFlag && !isFullAccess && !userData?.[item.companyFlag]) return false;
    return true;
  };

  const visibleMainNavItems = mainNavItems.filter(filterItem);
  const allOtherNavItems = otherNavItems.filter(filterItem);

  const isOtherActive = allOtherNavItems.some((item) => location.pathname.startsWith(item.to));
  const totalOtherBadge = unreadCount;

  return (
    <>
      {/* Others drawer overlay */}
      {othersOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setOthersOpen(false)}
        />
      )}

      {/* Others slide-up menu */}
      {othersOpen && (
        <div
          className="md:hidden fixed left-0 right-0 z-50"
          style={{ bottom: "calc(60px + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-3 mb-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {allOtherNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOthersOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-gray-100 last:border-0 ${
                    isActive ? "bg-indigo-50 text-indigo-600" : "text-gray-700 active:bg-gray-50"
                  }`
                }
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
                <span>{t(item.labelKey)}</span>
                {item.to === "/notifications" && unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-safe">
        <div className="flex justify-around py-1">
          {visibleMainNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOthersOpen(false)}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] relative min-h-[52px] min-w-0 active:bg-gray-100 rounded-lg transition-colors ${
                  isActive ? "text-indigo-600 font-medium" : "text-gray-500"
                }`
              }
            >
              <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
              </svg>
              <span className="w-full text-center truncate">{t(item.labelKey)}</span>
              {item.to === "/chat" && unreadChatCount > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadChatCount > 9 ? "9+" : unreadChatCount}
                </span>
              )}
            </NavLink>
          ))}

          {/* Others button */}
          <button
            onClick={() => setOthersOpen((o) => !o)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] relative min-h-[52px] min-w-0 active:bg-gray-100 rounded-lg transition-colors ${
              othersOpen || isOtherActive ? "text-indigo-600 font-medium" : "text-gray-500"
            }`}
          >
            <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="w-full text-center truncate">{t("return_home.nav.others")}</span>
            {!othersOpen && totalOtherBadge > 0 && (
              <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {totalOtherBadge > 9 ? "9+" : totalOtherBadge}
              </span>
            )}
          </button>
        </div>
      </nav>
    </>
  );
};
